/*
 * Copyright (C) 2021 DV Bern AG, Switzerland
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    inject
} from '@angular/core';
import {StateService} from '@uirouter/core';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {AuthServiceRS} from '../../../authentication/service/AuthServiceRS.rest';
import {TSSozialdienst} from '../../../models/sozialdienst/TSSozialdienst';
import {TSRoleUtil} from '../../../utils/TSRoleUtil';
import {SozialdienstRS} from '../../core/service/SozialdienstRS.rest';
import {DVEntitaetListItem} from '../../shared/interfaces/DVEntitaetListItem';

@Component({
    selector: 'dv-list-sozialdienst',
    templateUrl: './list-sozialdienst.component.html',
    styleUrls: ['./list-sozialdienst.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ListSozialdienstComponent implements OnInit {
    private readonly $state = inject(StateService);
    private readonly authServiceRS = inject(AuthServiceRS);
    private readonly sozialdienstRS = inject(SozialdienstRS);

    public hiddenDVTableColumns = [
        'institutionCount',
        'type',
        'remove',
        'gemeinde'
    ];

    public antragList$: Observable<DVEntitaetListItem[]>;

    public ngOnInit(): void {
        this.loadData();
    }

    public hatBerechtigungHinzufuegen(): boolean {
        return this.authServiceRS.isOneOfRoles(TSRoleUtil.getMandantRoles());
    }

    public addSozialdienst(): void {
        this.$state.go('sozialdienst.add');
    }

    /**
     * Route not yet implemented as it's gonna be part of another story
     */
    public open(id: string): void {
        this.$state.go('sozialdienst.edit', {sozialdienstId: id});
    }

    private loadData(): void {
        // For now only SuperAdmin
        const editPossible = this.authServiceRS.isOneOfRoles(
            TSRoleUtil.getAllRolesForSozialdienst()
        );
        this.antragList$ = this.getSozialdienstForPrincipal().pipe(
            map(sozialdienstList => {
                const entitaetListItems: DVEntitaetListItem[] = [];
                sozialdienstList.forEach(sozialdienst => {
                    const dvListItem = {
                        id: sozialdienst.id,
                        name: sozialdienst.name,
                        status: sozialdienst.status.toString(),
                        canEdit: editPossible,
                        canRemove: false
                    };
                    entitaetListItems.push(dvListItem);
                });
                return entitaetListItems;
            })
        );
    }

    private getSozialdienstForPrincipal(): Observable<TSSozialdienst[]> {
        if (
            this.authServiceRS.isOneOfRoles(TSRoleUtil.getSozialdienstRolle())
        ) {
            const sozialDienstList = [
                this.authServiceRS.getPrincipal().currentBerechtigung
                    .sozialdienst
            ];
            return of(sozialDienstList);
        }
        return this.sozialdienstRS.getSozialdienstList();
    }
}
